import { z } from 'zod';
import { uuidSchema } from './common.schema';
import { ProductDetailSchema, PRODUCT_STATUSES } from './products.schema';

// ---------------------------------------------------------------------------
// Hospital Catalog Product -- a hospital-owned copy of a master product
// Mirrors the DDL in hospital_product.md (Section 2.2, hospital copies)
//
// DynamoDB key pattern:
//   PK = PRODUCT#<product_id>#TENANT#<hospital_id>
//   SK = #METADATA
//
// GSI5 key (sparse — only on hospital copies):
//   GSI5PK = TENANT#<hospital_id>
//   GSI5SK = <product_name>
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Hospital-specific override fields (only present on catalog copies)
// ---------------------------------------------------------------------------
const hospitalCatalogFields = {
  /** UUID of the master product this copy derives from. */
  master_product_id:  uuidSchema,
  /** UUID of the hospital / tenant. */
  tenant_id:          z.string().min(1).max(128),
  /** Always true on hospital copies. */
  is_catalog_copy:    z.literal(true),
  /** "PRODUCT" when created from a master catalog entry; absent on manually-added products. */
  entity_type:        z.string().optional(),
  /** User UUID who added this product to the catalog. */
  catalog_added_by:   uuidSchema.nullable().optional(),
  /** ISO 8601 timestamp when it was added. */
  catalog_added_at:   z.string().nullable().optional(),
  /** Hospital prefers this product over alternatives. */
  is_preferred:       z.boolean().nullable().optional(),
  /** Hospital's internal SKU override (overrides master SKU in local workflows). */
  local_sku:          z.string().max(64).nullable().optional(),
  /** Units at which a low-stock alert fires. */
  reorder_threshold:  z.coerce.number().int().min(0).nullable().optional(),
  /** Flexible hospital-specific overrides (e.g. reconstitution ratio). */
  custom_settings:    z.record(z.string(), z.unknown()).nullable().optional(),
};

// ---------------------------------------------------------------------------
// HospitalCatalogProduct -- full catalog copy shape (master data + hospital overrides)
// ---------------------------------------------------------------------------
export const HospitalCatalogProductSchema = ProductDetailSchema.extend(hospitalCatalogFields);
export type HospitalCatalogProduct = z.infer<typeof HospitalCatalogProductSchema>;

// ---------------------------------------------------------------------------
// HospitalCatalogProductListItem -- lean shape for list views
// ---------------------------------------------------------------------------
export const HospitalCatalogProductListItemSchema = z.object({
  product_id:         uuidSchema,
  master_product_id:  uuidSchema,
  tenant_id:          z.string(),
  sku:                z.string().max(64),
  local_sku:          z.string().max(64).nullable().optional(),
  product_name:       z.string().max(255),
  display_name:       z.string().max(255).nullable().optional(),
  image_url:          z.string().url().nullable().optional(),
  brand_name:         z.string().max(255),
  form_name:          z.string().max(64),
  category_name:      z.array(z.string().max(128)),
  manufacturer_name:  z.string().max(255),
  type_of_product:    z.string().max(64).nullable().optional(),
  status:             z.enum(PRODUCT_STATUSES).nullable().optional(),
  is_active:          z.boolean().nullable().optional(),
  is_preferred:       z.boolean().nullable().optional(),
  is_catalog_copy:    z.literal(true),
  reorder_threshold:  z.number().int().min(0).nullable().optional(),
  catalog_added_at:   z.string().nullable().optional(),
  created_at:         z.string(),
  updated_at:         z.string(),
});
export type HospitalCatalogProductListItem = z.infer<typeof HospitalCatalogProductListItemSchema>;

// ---------------------------------------------------------------------------
// AddToCatalogRequest -- body for POST /inventory/products/:product_id/catalog
// ---------------------------------------------------------------------------
// AddOrCreateCatalogRequest -- body for POST /inventory/products/catalog
//
// Single endpoint handles two cases:
//   1. product_id provided + found in master → body optional, master data used as base
//   2. product_id absent (or not in master)  → sku + brand_name required,
//      a master record is created first, then the hospital catalog entry
//
// Runtime validation of the sku/brand_name requirement is done in the service layer.
// ---------------------------------------------------------------------------
export const AddOrCreateCatalogRequestSchema = z.object({
  /** UUID of the hospital adding the product. */
  hospital_id:     uuidSchema,
  /** Human-readable name of the hospital (used in notifications). */
  hospital_name:   z.string().max(255).optional(),
  /**
   * UUID of the product.
   * - If provided and found in master: master data is used; body fields are optional.
   * - If provided but NOT in master, or if absent: sku + brand_name become required
   *   and both a master record and a catalog entry are created.
   */
  product_id:      uuidSchema.optional(),
  /** Hospital's internal SKU override. */
  local_sku:       z.string().max(64).optional(),
  /** Mark as hospital-preferred immediately. */
  is_preferred:    z.boolean().default(false),
  /** Arbitrary hospital-specific config (e.g. recon ratio). */
  custom_settings: z.record(z.string(), z.unknown()).optional(),
  /**
   * Required when product_id is absent or not found in the master product list.
   * Ignored (master value takes precedence) when a master record exists.
   */
  sku:          z.string().min(1).max(64).optional(),
  product_name: z.string().min(1).max(255).optional(),
  brand_name:   z.string().min(1).max(255).optional(),
}).merge(
  ProductDetailSchema.partial().omit({
    product_id:            true,
    sku:                   true,
    product_name:          true,
    brand_name:            true,
    created_at:            true,
    updated_at:            true,
    tenant_id:             true,
    master_product_id:     true,
    is_catalog_copy:       true,
    catalog_added_by:      true,
    catalog_added_at:      true,
    is_preferred:          true,
    custom_settings:       true,
    attributes_updated_at: true,
    gsi1pk: true, gsi1sk: true,
    gsi2pk: true, gsi2sk: true,
    gsi3pk: true, gsi3sk: true,
    gsi4pk: true, gsi4sk: true,
    gsi5pk: true, gsi5sk: true,
    gsi6pk: true, gsi6sk: true,
  }),
);
export type AddOrCreateCatalogRequest = z.infer<typeof AddOrCreateCatalogRequestSchema>;

// ---------------------------------------------------------------------------
// UpdateCatalogProductRequest -- body for PATCH /inventory/products/:product_id/catalog
// All product fields are patchable, plus hospital-specific overrides.
// ---------------------------------------------------------------------------
export const UpdateCatalogProductRequestSchema = ProductDetailSchema.partial().omit({
  product_id:            true,
  created_at:            true,
  tenant_id:            true,
  master_product_id:     true,
  is_catalog_copy:       true,
  catalog_added_by:      true,
  catalog_added_at:      true,
  attributes_updated_at: true,
  gsi1pk: true, gsi1sk: true,
  gsi2pk: true, gsi2sk: true,
  gsi3pk: true, gsi3sk: true,
  gsi4pk: true, gsi4sk: true,
  gsi5pk: true, gsi5sk: true,
  gsi6pk: true, gsi6sk: true,
}).extend({
  /** Hospital's internal SKU override. */
  local_sku:         z.string().max(64).nullable().optional(),
  /** Units threshold to trigger a low-stock alert. */
  reorder_threshold: z.coerce.number().int().min(0).nullable().optional(),
});
export type UpdateCatalogProductRequest = z.infer<typeof UpdateCatalogProductRequestSchema>;

// ---------------------------------------------------------------------------
// HospitalCatalogQuery -- GET /inventory/products query params
// ---------------------------------------------------------------------------
const CATALOG_SORT_FIELDS = [
  'product_name',
  'sku',
  'status',
  'catalog_added_at',
  'updated_at',
] as const;

export type CatalogSortField = (typeof CATALOG_SORT_FIELDS)[number];

export const HospitalCatalogQuerySchema = z.object({
  /** Required: UUID or string ID of the hospital. */
  hospital_id:        uuidSchema,
  search:             z.string().max(200).optional(),
  status:             z.enum(PRODUCT_STATUSES).optional(),
  is_preferred:       z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  is_active:          z.enum(['true', 'false']).transform((v) => v === 'true').default(true),
  brand_name:         z.string().max(255).optional(),
  category_name:      z.array(z.string().max(128)).optional(),
  manufacturer_name:  z.string().max(255).optional(),
  form_name:          z.string().max(64).optional(),
  type_of_product:    z.string().max(64).optional(),
  sort_by:            z.enum(CATALOG_SORT_FIELDS).default('product_name'),
  sort_dir:           z.enum(['asc', 'desc']).default('asc'),
  page:               z.coerce.number().int().min(1).default(1),
  limit:              z.coerce.number().int().min(1).max(100).default(20),
});
export type HospitalCatalogQuery = z.infer<typeof HospitalCatalogQuerySchema>;

// ---------------------------------------------------------------------------
// MasterProductListItem -- full product detail shape for master (non-tenant) products
// Returned by GET /inventory/products/master
// DynamoDB key pattern: PK = PRODUCT#<product_id>, SK = METADATA
// Re-exports ProductDetailSchema so all product fields are included.
// ---------------------------------------------------------------------------
export { ProductDetailSchema as MasterProductListItemSchema } from './products.schema';
export type { ProductDetail as MasterProductListItem } from './products.schema';


